import { useState, useEffect } from 'react';
import { Zap, AlertCircle, CheckCircle2, Copy, RefreshCw, Loader2, ExternalLink, Send, ChevronDown, Download, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { domainApi, sitemapApi } from '@/lib/api';

interface IndexNowSettingsProps {
  domainId: string;
  domainName: string;
  isVerified: boolean;
  onStatusChange?: () => void;
}

export function IndexNowSettings({ domainId, domainName: _domainName, isVerified, onStatusChange }: IndexNowSettingsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState<'key' | 'url' | null>(null);
  const [keyVerified, setKeyVerified] = useState<boolean | null>(null);

  const [status, setStatus] = useState<{
    enabled: boolean;
    keyConfigured: boolean;
    key: string | null;
    keyFileUrl: string | null;
    targetDomain: string;
    targetKeyFileUrl: string | null;
  } | null>(null);

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const response = await domainApi.getIndexNowStatus(domainId);
      setStatus(response.indexNow);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load IndexNow status');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [domainId]);

  const handleEnable = async () => {
    setIsEnabling(true);
    setError(null);
    setSuccess(null);
    setKeyVerified(null);

    try {
      const response = await domainApi.enableIndexNow(domainId);
      setSuccess(response.message);
      await fetchStatus();
      onStatusChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable IndexNow');
    } finally {
      setIsEnabling(false);
    }
  };

  const handleRegenerate = async () => {
    if (!confirm('Regenerating the key will require updating the key file on your domain. Continue?')) {
      return;
    }

    setIsRegenerating(true);
    setError(null);
    setSuccess(null);
    setKeyVerified(null);

    try {
      const response = await domainApi.regenerateIndexNowKey(domainId);
      setSuccess(response.message);
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate key');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDisable = async () => {
    if (!confirm('Are you sure you want to disable IndexNow? You can re-enable it anytime.')) {
      return;
    }

    setIsDisabling(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await domainApi.disableIndexNow(domainId);
      setSuccess(response.message);
      await fetchStatus();
      onStatusChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable IndexNow');
    } finally {
      setIsDisabling(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await sitemapApi.submitToIndexNow(domainId);
      if (response.success) {
        const truncatedMsg = response.truncated ? ` (limited to 10,000 of ${response.totalUrlsInSitemaps})` : '';
        setSuccess(`Successfully submitted ${response.urlCount} page URL(s) to IndexNow${truncatedMsg}`);
      } else {
        setError(response.message || 'IndexNow submission failed');
      }
      onStatusChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit to IndexNow');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await domainApi.verifyIndexNowKey(domainId);
      setKeyVerified(response.verified);
      if (response.verified) {
        setSuccess(response.message);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setKeyVerified(false);
      setError(err instanceof Error ? err.message : 'Failed to verify key file');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopy = async (text: string, type: 'key' | 'url') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    if (!status?.key) return;
    const blob = new Blob([status.key], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${status.key}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      {/* Header - Clickable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-4 border-b flex items-center justify-between hover:from-purple-100 hover:to-blue-100 transition-colors"
      >
        <div className="text-left">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-600" />
            IndexNow Protocol
            {status?.enabled && !keyVerified && (
              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                Setup Required
              </span>
            )}
            {status?.enabled && keyVerified && (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                Verified
              </span>
            )}
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Instantly notify Bing, Yandex, and other search engines about content updates
          </p>
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-slate-500 transition-transform flex-shrink-0 ml-4',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Content - Collapsible */}
      {isExpanded && (
      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : !isVerified ? (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Domain Verification Required</p>
              <p className="text-sm text-amber-700 mt-1">
                Please verify your domain before enabling IndexNow.
              </p>
            </div>
          </div>
        ) : status?.enabled ? (
          <>
            {/* Enabled State */}
            <div className="space-y-5">
              {/* Setup Instructions */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">Setup Required</h3>
                <p className="text-sm text-blue-800 mb-3">
                  To use IndexNow, you need to upload the key file to your main domain. This proves ownership to search engines.
                </p>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Download the key file below</li>
                  <li>Upload it to the root of your domain: <code className="px-1.5 py-0.5 bg-blue-100 rounded font-mono text-xs">{status.targetKeyFileUrl}</code></li>
                  <li>Click "Verify Key" to confirm the file is accessible</li>
                </ol>
              </div>

              {/* Download Key File */}
              <div className="py-2">
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border rounded-lg hover:bg-slate-200 transition-colors font-medium text-slate-700"
                >
                  <Download className="w-4 h-4" />
                  Download IndexNow Key File
                </button>
                <p className="text-xs text-slate-500 mt-2">
                  Downloads <code className="px-1 py-0.5 bg-slate-100 rounded">{status.key}.txt</code>
                </p>
              </div>

              {/* Target Key File URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Upload Key File To
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center px-3 py-2 bg-slate-50 border rounded-lg font-mono text-sm">
                    <span className="truncate">{status.targetKeyFileUrl}</span>
                  </div>
                  <button
                    onClick={() => handleCopy(status.targetKeyFileUrl!, 'url')}
                    className="px-3 py-2 border rounded-lg hover:bg-slate-50 transition-colors"
                    title="Copy URL"
                  >
                    {copied === 'url' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                  <a
                    href={status.targetKeyFileUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 border rounded-lg hover:bg-slate-50 transition-colors"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Verification Status */}
              {keyVerified !== null && (
                <div className={cn(
                  "p-4 rounded-lg border flex items-start gap-3",
                  keyVerified
                    ? "bg-green-50 border-green-200"
                    : "bg-amber-50 border-amber-200"
                )}>
                  {keyVerified ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  )}
                  <div>
                    <p className={cn("font-medium", keyVerified ? "text-green-800" : "text-amber-800")}>
                      {keyVerified ? "Key Verified" : "Key Not Verified"}
                    </p>
                    <p className={cn("text-sm mt-1", keyVerified ? "text-green-700" : "text-amber-700")}>
                      {keyVerified
                        ? "Your key file is correctly configured. You can now submit URLs to IndexNow."
                        : "Please upload the key file to your domain and try again."}
                    </p>
                  </div>
                </div>
              )}

              {/* Learn More */}
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>Learn more about IndexNow:</span>
                <a
                  href="https://www.indexnow.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  indexnow.org
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <button
                  onClick={handleVerify}
                  disabled={isVerifying}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isVerifying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4" />
                  )}
                  Verify Key
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !keyVerified}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50",
                    keyVerified
                      ? "bg-purple-600 text-white hover:bg-purple-700"
                      : "bg-slate-200 text-slate-500 cursor-not-allowed"
                  )}
                  title={!keyVerified ? "Please verify your key file first" : undefined}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Submit to IndexNow
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={isRegenerating}
                  className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  {isRegenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Regenerate Key
                </button>
                <button
                  onClick={handleDisable}
                  disabled={isDisabling}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  {isDisabling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Disable IndexNow'
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Disabled State */}
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 border rounded-lg">
                <h3 className="font-medium text-slate-900 mb-2">What is IndexNow?</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Instantly notify search engines when your content changes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Supported by Bing, Yandex, Naver, Seznam.cz, and Yep</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Submit all URLs from your sitemaps with one click</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Requires one-time setup:</strong> upload a key file to your domain root</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleEnable}
                disabled={isEnabling}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
              >
                {isEnabling ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enabling...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Enable IndexNow
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}
        {success && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 text-green-700 text-sm">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {success}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
